import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../config';

const getAdminToken = () => localStorage.getItem('adminKey') || '';

const adminApi = axios.create({ baseURL: API_BASE_URL });
adminApi.interceptors.request.use((config) => {
  config.headers['x-admin-token'] = getAdminToken();
  return config;
});

const AdminConcentrationWatch = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [punishing, setPunishing] = useState(null);
  const [message, setMessage] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const res = await adminApi.get('/api/admin/concentration');
      if (res.data.success) {
        setData(res.data.data);
      }
    } catch (err) {
      console.error('Failed to fetch concentration data:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 10000);
    return () => clearInterval(interval);
  }, [fetchData]);

  const triggerNegativeEvent = async (item, severity) => {
    const multipliers = { mild: 0.85, moderate: 0.70, severe: 0.50 };
    const labels = { mild: '-15%', moderate: '-30%', severe: '-50%' };
    const descriptions = {
      mild: `Regulatory inquiry into ${item.ticker} operations`,
      moderate: `${item.ticker} reports disappointing earnings, misses estimates`,
      severe: `SEC investigation launched into ${item.ticker}, trading halted`
    };

    setPunishing(item.userId + item.stockId);
    try {
      await adminApi.post('/api/admin/trigger-event', {
        type: 'negative',
        description: descriptions[severity],
        multiplier: multipliers[severity],
        affectedStocks: [item.stockId]
      });
      setMessage({ type: 'success', text: `${labels[severity]} event triggered on ${item.ticker}` });
      setTimeout(() => fetchData(), 2000);
    } catch (err) {
      setMessage({ type: 'error', text: `Failed: ${err.message}` });
    } finally {
      setPunishing(null);
      setTimeout(() => setMessage(null), 4000);
    }
  };

  if (loading) {
    return <div style={{ padding: '20px', color: '#aaa' }}>Loading concentration data...</div>;
  }

  return (
    <div style={{ padding: '20px' }}>
      <h2 style={{ color: '#ff6b6b', marginBottom: '5px' }}>Concentration Watch</h2>
      <p style={{ color: '#888', fontSize: '13px', marginBottom: '20px' }}>
        Users with 50%+ of portfolio in a single stock. Trigger negative events to discourage all-in strategies.
      </p>

      {message && (
        <div style={{
          padding: '10px 15px',
          marginBottom: '15px',
          borderRadius: '8px',
          background: message.type === 'success' ? 'rgba(0,255,101,0.1)' : 'rgba(255,107,107,0.1)',
          border: `1px solid ${message.type === 'success' ? '#00ff65' : '#ff6b6b'}`,
          color: message.type === 'success' ? '#00ff65' : '#ff6b6b',
          fontSize: '14px'
        }}>
          {message.text}
        </div>
      )}

      {data.length === 0 ? (
        <div style={{
          padding: '40px',
          textAlign: 'center',
          background: 'rgba(0,255,101,0.05)',
          borderRadius: '12px',
          border: '1px solid rgba(0,255,101,0.2)'
        }}>
          <div style={{ fontSize: '48px', marginBottom: '10px' }}>&#10003;</div>
          <div style={{ color: '#00ff65', fontSize: '16px' }}>No concentrated positions detected</div>
          <div style={{ color: '#888', fontSize: '13px', marginTop: '5px' }}>All traders are diversified</div>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {data.map((item, i) => {
            const isPunishing = punishing === item.userId + item.stockId;
            return (
              <div key={i} style={{
                background: '#1a1a2e',
                borderRadius: '12px',
                padding: '16px',
                border: item.concentration >= 80
                  ? '1px solid rgba(255,107,107,0.4)'
                  : '1px solid rgba(255,165,0,0.3)'
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
                  <div>
                    <div style={{ color: '#fff', fontSize: '16px', fontWeight: 'bold' }}>
                      {item.username}
                      <span style={{ color: '#888', fontWeight: 'normal', fontSize: '13px', marginLeft: '8px' }}>
                        {item.shortId}
                      </span>
                    </div>
                    <div style={{ color: '#aaa', fontSize: '13px', marginTop: '4px' }}>
                      Portfolio: ${item.portfolioValue.toFixed(2)} | Cash: ${item.balance.toFixed(2)}
                    </div>
                  </div>
                  <div style={{
                    background: item.concentration >= 80 ? 'rgba(255,107,107,0.2)' : 'rgba(255,165,0,0.2)',
                    color: item.concentration >= 80 ? '#ff6b6b' : '#ffa500',
                    padding: '4px 12px',
                    borderRadius: '20px',
                    fontSize: '14px',
                    fontWeight: 'bold'
                  }}>
                    {item.concentration}%
                  </div>
                </div>

                {/* Concentration bar */}
                <div style={{ marginBottom: '12px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                    <span style={{ color: '#fff', fontSize: '14px', fontWeight: 'bold' }}>
                      {item.ticker} - {item.stockName}
                    </span>
                    <span style={{ color: '#aaa', fontSize: '13px' }}>
                      ${item.positionValue.toFixed(2)}
                    </span>
                  </div>
                  <div style={{
                    height: '6px',
                    background: 'rgba(255,255,255,0.1)',
                    borderRadius: '3px',
                    overflow: 'hidden'
                  }}>
                    <div style={{
                      height: '100%',
                      width: `${Math.min(item.concentration, 100)}%`,
                      background: item.concentration >= 80
                        ? 'linear-gradient(90deg, #ff6b6b, #ff4444)'
                        : 'linear-gradient(90deg, #ffa500, #ff8c00)',
                      borderRadius: '3px',
                      transition: 'width 0.3s'
                    }} />
                  </div>
                </div>

                {/* Action buttons */}
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                  <button
                    onClick={() => triggerNegativeEvent(item, 'mild')}
                    disabled={isPunishing}
                    style={{
                      padding: '6px 14px',
                      borderRadius: '6px',
                      border: '1px solid rgba(255,165,0,0.4)',
                      background: 'rgba(255,165,0,0.1)',
                      color: '#ffa500',
                      cursor: isPunishing ? 'not-allowed' : 'pointer',
                      fontSize: '12px',
                      opacity: isPunishing ? 0.5 : 1
                    }}
                  >
                    Mild (-15%)
                  </button>
                  <button
                    onClick={() => triggerNegativeEvent(item, 'moderate')}
                    disabled={isPunishing}
                    style={{
                      padding: '6px 14px',
                      borderRadius: '6px',
                      border: '1px solid rgba(255,107,107,0.4)',
                      background: 'rgba(255,107,107,0.1)',
                      color: '#ff6b6b',
                      cursor: isPunishing ? 'not-allowed' : 'pointer',
                      fontSize: '12px',
                      opacity: isPunishing ? 0.5 : 1
                    }}
                  >
                    Moderate (-30%)
                  </button>
                  <button
                    onClick={() => triggerNegativeEvent(item, 'severe')}
                    disabled={isPunishing}
                    style={{
                      padding: '6px 14px',
                      borderRadius: '6px',
                      border: '1px solid rgba(255,0,0,0.4)',
                      background: 'rgba(255,0,0,0.1)',
                      color: '#ff4444',
                      cursor: isPunishing ? 'not-allowed' : 'pointer',
                      fontSize: '12px',
                      fontWeight: 'bold',
                      opacity: isPunishing ? 0.5 : 1
                    }}
                  >
                    Severe (-50%)
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <div style={{ marginTop: '15px', textAlign: 'right', color: '#666', fontSize: '12px' }}>
        Auto-refreshes every 10s
      </div>
    </div>
  );
};

export default AdminConcentrationWatch;
